import React from "react";
import {
  Card,
  Input,
  Select,
  Divider,
  Typography,
  Space,
  Button,
  Switch,
  DatePicker,
} from "antd";
import { WORKSPACE_NAME_HINT } from "../config/constants";
import { foldSearch } from "../../../../utils/searchFold";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const Step1 = ({
  workspaceName,
  workspaceNameInvalid,
  workspaceNameTaken,
  handleWorkspaceNameChange,
  clientId,
  handleClientIdChange,
  clientOptions,
  newRegionName,
  handleNewRegionNameChange,
  handleNewRegionNamePressEnter,
  addRegion,
  customRegions,
  removeRegion,
  allClientMarkets,
  updateRegionMarkets,
  marketOptions,
  // New props for additional requirements
  refreshPeriods,
  handleRefreshPeriodsChange,
  refreshPeriodOptions,
  refreshUnavailable,
  refreshFailed,
  autoRefresh,
  handleAutoRefreshChange,
  contractMonthRange,
  handleContractMonthRangeChange,
  clientIdsLoading,
  marketsLoading,
  refreshLoading,
}) => {
  // The name is written verbatim as id_client, so a taken one would land on an
  // existing workspace. Both problems share the hint row, since only one can
  // apply at a time.
  const nameHasError = workspaceNameInvalid || workspaceNameTaken;

  // Why the period list is empty decides what the operator should do: no data
  // for this client means pick a different one, a failed lookup means retry.
  let refreshEmptyContent = "Select a client to load its refresh periods.";
  if (refreshLoading) {
    refreshEmptyContent = "Loading refresh periods...";
  } else if (refreshUnavailable) {
    refreshEmptyContent =
      "No refresh periods found for this client — it has no data yet, so a " +
      "workspace cannot be built for it.";
  } else if (refreshFailed) {
    refreshEmptyContent =
      "Could not load refresh periods. Reload the page and try again.";
  } else if (clientId) {
    refreshEmptyContent = "No refresh periods found for this client.";
  }

  return (
    <Card title="Basic Information">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
            Workspace Name *
          </label>
          <Input
            allowClear
            status={nameHasError ? "error" : ""}
            value={workspaceName}
            onChange={handleWorkspaceNameChange}
            placeholder="e.g. pepsi_uk"
          />
          <Text
            type={nameHasError ? "danger" : "secondary"}
            style={{ fontSize: 12, display: "block", marginTop: 4 }}
          >
            {workspaceNameTaken
              ? `Workspace "${workspaceName.trim()}" already exists. Pick a different name.`
              : WORKSPACE_NAME_HINT}
          </Text>
        </div>

        <div>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
            Client *
          </label>
          <Select
            allowClear
            loading={clientIdsLoading}
            placeholder="Select client"
            value={clientId || undefined}
            onChange={handleClientIdChange}
            style={{ width: "100%" }}
            options={clientOptions}
            notFoundContent={
              clientIdsLoading
                ? "Loading clients..."
                : "No clients available — reload the page or check your session."
            }
          />
        </div>

        <Divider
          orientation="left"
          style={{ margin: "16px 0 8px 0", fontSize: "14px", fontWeight: 500 }}
        >
          Configuration Settings
        </Divider>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          <div>
            <label
              style={{ display: "block", marginBottom: 4, fontWeight: 500 }}
            >
              Refresh Periods
            </label>
            <Select
              mode="multiple"
              allowClear
              loading={refreshLoading}
              placeholder={
                refreshLoading
                  ? "Loading refresh periods..."
                  : "Select refresh periods"
              }
              value={refreshPeriods}
              onChange={handleRefreshPeriodsChange}
              style={{ width: "100%" }}
              options={refreshPeriodOptions}
              maxTagCount="responsive"
              showSearch
              filterOption={(input, option) =>
                foldSearch(option?.label).includes(foldSearch(input))
              }
              notFoundContent={refreshEmptyContent}
            />
            {(refreshUnavailable || refreshFailed) && (
              <Text
                type="danger"
                style={{ fontSize: 12, display: "block", marginTop: 4 }}
              >
                {refreshEmptyContent}
              </Text>
            )}
          </div>

          <div>
            <label
              style={{ display: "block", marginBottom: 4, fontWeight: 500 }}
            >
              Auto Refresh
            </label>
            <div style={{ paddingTop: 4 }}>
              <Switch
                checked={autoRefresh}
                onChange={handleAutoRefreshChange}
                checkedChildren="Enabled"
                unCheckedChildren="Disabled"
              />
            </div>
          </div>
        </div>

        <div>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
            Contract Month Range
          </label>
          <RangePicker
            picker="month"
            value={contractMonthRange}
            onChange={handleContractMonthRangeChange}
            style={{ width: "100%" }}
            placeholder={["Start Month", "End Month"]}
            format="YYYY-MM"
          />
        </div>
      </div>

      <Divider
        orientation="left"
        style={{ marginTop: 24, marginBottom: 16 }}
      >
        Custom Regions
      </Divider>
      {!clientId && <Text type="secondary">Select a client first.</Text>}
      {clientId && (
        <>
          <Space
            style={{ marginBottom: 12 }}
            wrap
          >
            <Input
              size="small"
              style={{ width: 200 }}
              placeholder="Region name"
              value={newRegionName}
              onChange={handleNewRegionNameChange}
              onPressEnter={handleNewRegionNamePressEnter}
            />
            <Button
              size="small"
              type="dashed"
              onClick={addRegion}
            >
              Add Region
            </Button>
          </Space>
          {Object.keys(customRegions).length === 0 && (
            <div style={{ color: "#999", marginBottom: 12 }}>
              No custom regions yet.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {Object.entries(customRegions).map(([rid, r]) => {
              const handleRemove = () => removeRegion(rid);
              const handleMarketsChange = (vals) =>
                updateRegionMarkets(rid, vals);

              return (
                <Card
                  key={`region-${rid}`}
                  size="small"
                  title={r.name}
                  extra={
                    <Button
                      size="small"
                      type="link"
                      danger
                      onClick={handleRemove}
                    >
                      Delete
                    </Button>
                  }
                >
                  <Text style={{ fontSize: 12 }}>
                    Select markets (client total: {allClientMarkets.length})
                  </Text>
                  <Select
                    key={`markets-${rid}`}
                    mode="multiple"
                    allowClear
                    loading={marketsLoading}
                    style={{ width: "100%", marginTop: 8 }}
                    value={r.markets || []}
                    onChange={handleMarketsChange}
                    placeholder={
                      marketsLoading ? "Loading markets..." : "Select markets"
                    }
                    options={marketOptions}
                  />
                </Card>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
};

export default Step1;
